/**
 * URL-backed source.
 *
 * Covers everything that lives behind an HTTP URL — chat attachments,
 * tool-output files, library / agent-served files. The differences between
 * those callers are surfaced as options on this factory rather than
 * separate adapter classes:
 *
 *   - `fileId` lets PDF previews fetch an inline-signed URL from
 *     `/api/files/attachments/:id/preview-url` so the iframe doesn't
 *     trigger a download (see issue #989). Only present for files
 *     registered in `chat_attachments`.
 *
 *   - `authRequired` switches text fetches to use `getAuthHeader()` so
 *     workspace-scoped routes (`/api/workspaces/:id/files/content`)
 *     return content instead of 401-ing. Public URLs (presigned Spaces,
 *     CDN) leave this false.
 *
 * The source itself never talks to the renderer — renderers only call
 * the methods exposed on this object. Anything URL-specific stays here.
 */
import { getAuthHeader } from "@/utils";

import type { FileKind, FileNotFoundError, FileSource } from "../types";

export interface UrlSourceOptions {
  /** Direct URL to the file (presigned S3 URL, public CDN, server route, …). */
  url: string;

  /** Display name; defaults to the URL's basename if you omit it. */
  filename?: string;

  /** MIME type if you know it. Renderer dispatch infers from the extension when missing. */
  contentType?: string;

  /** Byte size for the footer; defaults to "unknown" if omitted. */
  size?: number;

  /**
   * Skip the registry's MIME / extension inference and pin the renderer.
   * Useful for tool outputs that know they're spreadsheets, etc.
   */
  kind?: FileKind;

  /**
   * `chat_attachments.id`. When present, PDF previews ask the server for
   * an inline-signed URL — the only path that produces a `Content-
   * Disposition: inline` response and stops Chromium / Edge from
   * downloading PDFs in iframes.
   */
  fileId?: string;

  /**
   * Set true when fetching the URL needs the session cookie / bearer
   * header (e.g. workspace-scoped server routes). Public URLs (presigned
   * Spaces) leave this false.
   */
  authRequired?: boolean;

  /**
   * Produces a URL that authenticates WITHOUT a request header — i.e. a
   * short-lived `?token=` appended by the server's content-token endpoint.
   * Needed for "open in new tab" / "download", which go through
   * `window.open` and so can't carry the bearer header that `authRequired`
   * routes demand (the route gained auth as a security fix; #1125/#1126).
   * When omitted, the external/download URL falls back to the raw `url`
   * (correct for public presigned URLs that need no token).
   */
  resolveTokenizedUrl?: () => Promise<string>;
}

export function urlSource(opts: UrlSourceOptions): FileSource {
  const filename =
    opts.filename || opts.url.split("?")[0]?.split("/").pop() || "file";

  /**
   * Build a `RequestInit` carrying the bearer auth header when the route
   * needs it. `getAuthHeader()` returns the *string* `"Bearer …"` (or
   * null when no session), so we set it on the `Authorization` header
   * rather than spreading — spreading a string would silently produce
   * an unusable header bag.
   */
  function buildFetchInit(): RequestInit | undefined {
    if (!opts.authRequired) return undefined;
    const auth = getAuthHeader();
    return auth ? { headers: { Authorization: auth } } : undefined;
  }

  /**
   * Turn a failed file fetch into a user-facing error. A 404 specifically means
   * the file is gone — a workspace file card can outlive its file (the agent
   * deleted or moved it later in the same conversation), and surfacing a raw
   * "404 Not Found" left the user staring at a card they could open but that
   * errored cryptically. Say plainly that the file no longer exists.
   */
  function fetchError(res: Response): Error {
    if (res.status === 404) {
      const err = new Error(
        `"${filename}" no longer exists in the workspace — it may have been deleted or moved after this message was sent.`,
      );
      /**
       * Flag a 404 as a genuine "file is gone" so text renderers don't fall
       * back to iframing the URL. The iframe fallback exists for CORS-blocked
       * fetches (URL unreadable by fetch but loadable in a frame); for a 404 the
       * frame would just re-load the same URL and render the server's JSON error
       * body — the exact ugly output this message replaces. See
       * `UrlIframeFallback`, which checks this flag.
       */
      (err as FileNotFoundError).notFound = true;
      return err;
    }
    return new Error(`Failed to fetch file (${res.status} ${res.statusText})`);
  }

  /**
   * The preview-url re-sign endpoint is workspace-scoped and protected
   * by `authenticateToken`. We always pass the bearer header for it,
   * independent of `authRequired` — the option controls *file URL*
   * authentication, not server API calls.
   */
  function buildAuthInit(): RequestInit | undefined {
    const auth = getAuthHeader();
    return auth ? { headers: { Authorization: auth } } : undefined;
  }

  return {
    id: opts.fileId ?? opts.url,
    filename,
    contentType: opts.contentType,
    kind: opts.kind,
    size: opts.size,
    capabilities: {
      canDownload: true,
      canOpenExternal: true,
    },

    getText: async () => {
      const res = await fetch(opts.url, buildFetchInit());
      if (!res.ok) {
        throw fetchError(res);
      }
      return res.text();
    },

    getBlobUrl: async (blobOpts) => {
      /**
       * Auth-required URLs can't be loaded by an iframe / <img>
       * directly — the browser doesn't carry the bearer header on
       * those tag-level fetches, so the server 401s. Fetch the bytes
       * ourselves with auth and hand back a blob URL the iframe can
       * load same-origin.
       *
       * The hook that consumes this URL revokes it on unmount via
       * `URL.createObjectURL`/`URL.revokeObjectURL`, so we don't leak
       * memory.
       */
      if (opts.authRequired) {
        const res = await fetch(opts.url, buildFetchInit());
        if (!res.ok) {
          throw fetchError(res);
        }
        const raw = await res.blob();
        /**
         * Normalize the blob's MIME if the caller declared one — some
         * server routes serve everything as `application/octet-stream`,
         * which makes browsers download PDFs even when loaded via blob
         * URL. Re-wrap with the explicit content type when known.
         */
        const blob = opts.contentType && raw.type !== opts.contentType
          ? new Blob([raw], { type: opts.contentType })
          : raw;
        return URL.createObjectURL(blob);
      }

      /**
       * Public-URL inline preview: PDF / image previews that need
       * `Content-Disposition: inline` go through the server's re-sign
       * endpoint — it's the only place that can apply the disposition
       * before AWS signs the URL. Falls back to the raw URL when we
       * don't have a `fileId` to key the re-sign by (legacy
       * attachments, non-chat upload surfaces) — the user may still
       * see a download in that case, which is the same behaviour as
       * before this module existed.
       */
      if (blobOpts?.inline && opts.fileId) {
        try {
          const res = await fetch(
            `/api/files/attachments/${opts.fileId}/preview-url`,
            buildAuthInit(),
          );
          if (res.ok) {
            const json = (await res.json()) as { data?: { url?: string } };
            if (json.data?.url) return json.data.url;
          }
        } catch {
          // Fall through to the raw URL — better a download than a hang.
        }
      }
      return opts.url;
    },

    getBytes: async () => {
      const res = await fetch(opts.url, buildFetchInit());
      if (!res.ok) {
        throw fetchError(res);
      }
      return res.arrayBuffer();
    },

    // Open-external and download can't send an auth header (they hand the URL
    // to `window.open`), so prefer a header-free tokenized URL when the source
    // provides one. Public/presigned URLs leave `resolveTokenizedUrl` unset and
    // fall back to the raw URL.
    resolveDownloadUrl: async () =>
      opts.resolveTokenizedUrl ? await opts.resolveTokenizedUrl() : opts.url,
    resolveExternalUrl: async () =>
      opts.resolveTokenizedUrl ? await opts.resolveTokenizedUrl() : opts.url,
  };
}
